"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { postsAPI, socialUtils, handleApiError } from "../../services/api";
import ChannelPostReactions from "../channels/ChannelPostReactions";
import ChannelPostComments from "../channels/ChannelPostComments";
import MultiMediaDisplay from "../social/posts/MultiMediaDisplay";
import PostReactionsModal from "../social/posts/PostReactionsModal";
import AdoptionPostEmbed from "../social/posts/AdoptionPostEmbed";
import { usePostModal } from "../../contexts/PostModalContext";
import { adoptionAPI } from "../../services/adoptionAPI";
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";
import "../channels/ChannelPost.css";
import "../channels/ChannelPostReactions.css";
import "../channels/ChannelPostComments.css";
import "./PostDetailModal.css";

const PostDetailModal = () => {
  const { t, i18n } = useTranslation();
  const { getUserType, getProfileImageUrl, user: currentUser } = useAuth();
  const { isOpen, selectedPostId, highlightCommentId, closePostModal, openPostModal } = usePostModal();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showReactionsModal, setShowReactionsModal] = useState(false);

  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8',
      'VETERINARIAN': '#00D4B3',
      'SERVICE_PROVIDER': '#D65745',
      'SERVICE_PROVIDER': '#D65745',
      'CLINIC_MODERATOR': '#2A8AD2',
    };
    return userTypeConfig[userType] || '#8548A8';
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  // Fetch post data when modal opens
  useEffect(() => {
    if (isOpen && selectedPostId) {
      fetchPost();
    } else {
      // Reset state when modal closes
      setPost(null);
      setError(null);
    }
  }, [isOpen, selectedPostId]);

  // Handle keyboard events and body scroll without jumping the page
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      // Preserve current scroll position while locking body scroll
      const savedScrollY = window.scrollY;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalTop = document.body.style.top;
      const originalOverflow = document.body.style.overflow;

      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);

        // Restore body scroll and previous scroll position
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.top = originalTop;
        document.body.style.overflow = originalOverflow;

        window.scrollTo({
          top: savedScrollY,
          behavior: 'instant',
        });
      };
    }

    // If modal is not open, make sure body styles are reset
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && isOpen) {
      closePostModal();
    }
  };

  const fetchPost = async () => {
    if (!selectedPostId) return;

    setIsLoading(true);
    setError(null);

    try {
      let response;
      try {
        response = await postsAPI.getPost(selectedPostId);
      } catch (postErr) {
        console.warn("Failed to fetch as regular post, trying adoption post fallback:", postErr);
        // Fallback: Try fetching as an adoption post
        const adoptionResponse = await adoptionAPI.getAdoptionPost(selectedPostId);
        
        if (adoptionResponse.data) {
          const adoptData = adoptionResponse.data.data || adoptionResponse.data;
          // Mock a social post structure for the adoption post
          const mockPost = {
            id: adoptData.id,
            author: {
              id: adoptData.user_id || adoptData.user?.id || 0,
              name: adoptData.user?.name || "Unknown User",
              username: adoptData.user?.username || "unknown",
              avatar: adoptData.user?.avatar || adoptData.user?.profile_image_url || "/icons/person.png",
              user_type: adoptData.user?.user_type || "PET_GUARDIAN",
            },
            content: adoptData.about_pet || "",
            created_at: adoptData.created_at,
            type: "adoption",
            post_type: "adoption",
            adoption_post_id: adoptData.id,
            adoption_post: adoptData,
            media: (adoptData.pet_info?.media || adoptData.pets?.[0]?.media || []).map(m => ({
              url: m.url || m,
              file_type: "image"
            }))
          };
          
          setPost(mockPost);
          setIsLoading(false);
          return;
        }
        throw postErr; // Re-throw if adoption fallback also fails
      }

      if (response.data && response.data.data) {
        // Transform post data using socialUtils
        const transformedPost = socialUtils.transformPostData(response.data.data);

        if (transformedPost) {
          // Ensure post has all required fields for display
          const formattedPost = {
            ...transformedPost,
            author: {
              id: transformedPost.author?.id || transformedPost.user_id || 0,
              name: transformedPost.author?.name || transformedPost.author?.username || "Unknown User",
              username: transformedPost.author?.username || `@user${transformedPost.author?.id || 0}`,
              profileImage: transformedPost.author?.avatar || transformedPost.author?.profile_image_url || "/icons/person.png",
              userType: transformedPost.author?.user_type || transformedPost.author?.userType || 'PET_GUARDIAN',
            },
            createdAt: transformedPost.timestamp || transformedPost.created_at || "Just now",
            comments: transformedPost.reactions?.comments || transformedPost.comments_count || transformedPost.comments || 0,
            likes: transformedPost.reactions?.likes || transformedPost.likes || 0,
            shares: transformedPost.reactions?.shares || transformedPost.shares || 0,
            user_reaction_type: transformedPost.user_reaction_type || null,
            reaction_counts: transformedPost.reaction_counts || {},
            reactions_count: transformedPost.reactions_count || transformedPost.reactions?.likes || transformedPost.likes || 0,
            images: transformedPost.media ? transformedPost.media.map(m => m.url) : (transformedPost.images || []),
            type: transformedPost.type || transformedPost.post_type || 'post',
            // Preserve adoption post data with metadata fallback - check both main post and reposted post
            adoption_post_id: transformedPost.adoption_post_id || 
                             transformedPost.reposted_post?.adoption_post_id ||
                             response.data.data?.adoption_post_id || 
                             response.data.data?.reposted_post?.adoption_post_id ||
                             response.data.data?.metadata?.adoption_post_id || 
                             response.data.data?.metadata?.adoption_post?.id || null,
            adoption_post: transformedPost.adoption_post || 
                          transformedPost.reposted_post?.adoption_post ||
                          response.data.data?.adoption_post || 
                          response.data.data?.reposted_post?.adoption_post ||
                          response.data.data?.metadata?.adoption_post || null,
          };

          setPost(formattedPost);
        } else {
          setError("Failed to transform post data");
        }
      } else {
        setError("Post not found");
      }
    } catch (err) {
      console.error("Error fetching post details:", err);
      if (err.response?.status === 404) {
        setError("channelPost.notFound");
      } else {
        setError(handleApiError(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePostUpdate = (updatedPost) => {
    setPost(prevPost => ({
      ...prevPost,
      ...updatedPost,
    }));
  };

  const copyTextToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      // fall through
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      textarea.style.left = "-9999px";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);
      return successful;
    } catch (err) {
      return false;
    }
  };

  const handleShare = async () => {
    if (!post) return;

    try {
      const { sharesAPI } = await import("../../services/api");
      const response = await sharesAPI.sharePost(post.id);
      const responseData = response?.data?.data || response?.data || {};

      // Get share URL from backend response (preferred) or fallback to constructing it
      const shareUrl = responseData?.share_url || responseData?.url ||
        `${window.location.origin}/social/profile/posts/${post.id}`;

      // Copy URL to clipboard
      const didCopy = await copyTextToClipboard(shareUrl);
      if (didCopy) {
        showSuccessToast(t("postFeed.shareActions.copySuccess") || "Link copied to clipboard!");
      } else {
        showErrorToast(t("postFeed.shareActions.copyError") || "Failed to copy link");
        window.prompt(t("postFeed.shareActions.manualCopyPrompt") || "Copy this link:", shareUrl);
      }

      // Update share count from backend response
      const shareCount = responseData?.shares_count || responseData?.shares ||
        ((post.shares || 0) + 1);

      handlePostUpdate({
        shares: shareCount,
      });
    } catch (error) {
      console.error("Error sharing post:", error);
      // Even if API fails, try to copy a fallback URL
      const fallbackUrl = `${window.location.origin}/social/profile/posts/${post.id}`;
      try {
        const didCopy = await copyTextToClipboard(fallbackUrl);
        if (didCopy) {
          showSuccessToast(t("postFeed.shareActions.copySuccess") || "Link copied to clipboard!");
        } else {
          showErrorToast(t("postFeed.shareActions.copyError") || "Failed to copy link");
        }
      } catch (copyErr) {
        showErrorToast(t("postFeed.shareActions.copyError") || "Failed to copy link");
      }
    }
  };

  const isArabicText = (text) => {
    if (!text || text.trim() === "") return false;
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return arabicRegex.test(text);
  };

  const formatContent = (content) => {
    if (!content) return "";

    const isArabic = isArabicText(content);

    // Linkify URLs robustly
    const urlRegex = /(<a\b[^>]*>[\s\S]*?<\/a>)|(<[^>]+>)|(https?:\/\/[^\s<]+)/gi;
    const linkifiedContent = content.replace(urlRegex, (match, aTag, otherTag, url) => {
      if (aTag) return aTag;
      if (otherTag) return otherTag;
      if (url) return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #00D4B3; text-decoration: underline;" onClick="event.stopPropagation()">${url}</a>`;
      return match;
    });

    // Check if originally the content had HTML tags
    const hasHTML = /<[^>]+>/.test(content);

    if (hasHTML) {
      return (
        <div
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            lineHeight: "1.5",
          }}
          dangerouslySetInnerHTML={{ __html: linkifiedContent }}
        />
      );
    }

    return linkifiedContent.split("\n").map((line, index) => {
      if (line.trim().startsWith("•")) {
        return (
          <div
            key={index}
            style={{
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: line }} />
          </div>
        );
      }
      return (
        <div
          key={index}
          style={{
            direction: isArabic ? "rtl" : "ltr",
            textAlign: isArabic ? "right" : "left",
            marginBottom: line.trim() ? "8px" : "4px",
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: line || "\u00A0" }} />
        </div>
      );
    });
  };


  if (!isOpen) return null;

  const modalContent = (
    <div
      className="post-detail-modal-overlay"
      onClick={closePostModal}
      dir={i18n.language?.startsWith("ar") ? "rtl" : "ltr"}
    >
      <div
        className="post-detail-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          '--user-type-color': userTypeColor,
          maxWidth: (post?.adoption_post_id || post?.adoption_post?.id || post?.reposted_post?.adoption_post_id || post?.reposted_post?.adoption_post?.id || post?.metadata?.adoption_post_id || post?.metadata?.adoption_post?.id) ? '900px' : '700px'
        }}
      >
        {/* Close Button */}
        <button
          className="post-detail-modal-close"
          onClick={closePostModal}
          aria-label={t("common.close", "Close")}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Modal Content */}
        <div className="post-detail-modal-content">
          {isLoading ? (
            <div className="post-detail-modal-loading">
              <div className="post-detail-modal-spinner"></div>
              <p>{t("common.loading", "Loading...")}</p>
            </div>
          ) : error ? (
            <div className={`post-detail-modal-error ${error === "channelPost.notFound" ? "not-found" : ""}`}>
              <p>{t(error, error)}</p>
              {error !== "channelPost.notFound" && (
                <button onClick={fetchPost} className="post-detail-modal-retry">
                  {t("common.retry", "Retry")}
                </button>
              )}
            </div>
          ) : post ? (
            <div className="post-detail-modal-post">
              {/* Post Header */}
              <div className="post-detail-modal-header">
                <div className="post-detail-modal-author">
                  <img
                    src={post.author.profileImage || "/icons/person.png"}
                    alt={post.author.name}
                    className="post-detail-modal-avatar"
                    onError={(e) => {
                      e.target.src = "/icons/person.png";
                    }}
                  />
                  <div className="post-detail-modal-author-info">
                    <div className="post-detail-modal-author-line">
                      <span className="post-detail-modal-author-name">{post.author.name}</span>
                      {(post.reposted_post || post.original_post || post.is_repost || post.repost_id || post.type === 'repost') && (
                        <span className="post-detail-modal-reposted-indicator">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 1l4 4-4 4" />
                            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                            <path d="M7 23l-4-4 4-4" />
                            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                          </svg>
                          {t("postFeed.repost.reposted", "reposted")}
                        </span>
                      )}
                    </div>
                    <span className="post-detail-modal-time">{post.createdAt}</span>
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <div className="post-detail-modal-body">
                {/* Repost Display */}
                {(() => {
                  const originalPost = post.reposted_post || post.original_post;
                  const isRepost = post.type === 'repost' || post.is_repost || !!originalPost;

                  if (!isRepost || !originalPost) return null;

                  const repostResponse = post.repost_response_text || post.response_text || post.repost_comment || (post.is_repost ? post.content : null);
                  const originalAuthor = originalPost.author;
                  const originalPostId = originalPost.id;

                  return (
                    <div className="post-detail-modal-repost-container">
                      {/* Reposter's Response/Comment */}
                      {repostResponse && (
                        <div
                          className="post-detail-modal-repost-comment"
                          dir={isArabicText(repostResponse) ? "rtl" : "ltr"}
                        >
                          {formatContent(repostResponse)}
                        </div>
                      )}

                      {/* Original Post Embed */}
                      <div
                        className="post-detail-modal-original-post"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (originalPostId) {
                            openPostModal(String(originalPostId));
                          }
                        }}
                      >
                        {/* Original Post Header */}
                        <div className="post-detail-modal-original-header">
                          <img
                            src={originalAuthor?.avatar || "/icons/person.png"}
                            alt={originalAuthor?.name}
                            className="post-detail-modal-original-avatar"
                            style={{ '--original-user-type-color': socialUtils.getUserTypeColor(originalAuthor?.userType) }}
                            onError={(e) => {
                              e.target.src = "/icons/person.png";
                            }}
                          />
                          <div className="post-detail-modal-original-author-info">
                            <span className="post-detail-modal-original-author-name">
                              {originalAuthor?.name}
                            </span>
                            <span className="post-detail-modal-original-time">
                              {originalPost.timestamp || originalPost.created_at}
                            </span>
                          </div>
                        </div>

                        {/* Original Post Content */}
                        {originalPost.content && (
                          <div
                            className="post-detail-modal-original-content"
                            dir={isArabicText(originalPost.content) ? "rtl" : "ltr"}
                          >
                            {formatContent(originalPost.content)}
                          </div>
                        )}

                        {/* Original Post Media */}
                        {originalPost.media && originalPost.media.length > 0 && (
                          <div className="post-detail-modal-original-media">
                            <MultiMediaDisplay
                              media={originalPost.media}
                              style={{ borderRadius: "8px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Regular Post Content (if not a repost) */}
                {post.type !== 'repost' && !post.is_repost && post.content && (
                  <div
                    className="post-detail-modal-text"
                    dir={isArabicText(post.content) ? 'rtl' : 'ltr'}
                  >
                    {formatContent(post.content)}
                  </div>
                )}

                {/* Regular Post Media (if not a repost) */}
                {post.type !== 'repost' && !post.is_repost && post.media && post.media.length > 0 && (
                  <div className="post-detail-modal-media">
                    <MultiMediaDisplay
                      media={post.media}
                      style={{
                        borderRadius: "12px",
                        overflow: "hidden",
                      }}
                    />
                  </div>
                )}

                {/* Adoption Post Embed */}
                {(() => {
                  const adoptionPostId = post.adoption_post_id || 
                                       post.adoption_post?.id || 
                                       post.reposted_post?.adoption_post_id || 
                                       post.reposted_post?.adoption_post?.id ||
                                       post.metadata?.adoption_post_id || 
                                       post.metadata?.adoptionPostId || 
                                       post.metadata?.adoption_post?.id;
                  
                  const adoptionPostData = post.adoption_post || 
                                         post.reposted_post?.adoption_post || 
                                         post.metadata?.adoption_post || 
                                         post.metadata?.adoptionPost || 
                                         null;

                  if (!adoptionPostId) return null;

                  return (
                    <div style={{ marginTop: post.content || post.media?.length > 0 ? "16px" : "0", marginBottom: "16px" }}>
                      <AdoptionPostEmbed
                        adoptionPostId={adoptionPostId}
                        adoptionPost={adoptionPostData}
                        currentUser={currentUser}
                        onAdoptionRequestSent={(id) => {
                          // Optionally update state if needed
                        }}
                      />
                    </div>
                  );
                })()}

                {/* Post Footer */}
                <div className="channel-post-footer" style={{ marginTop: "16px" }}>
                  <ChannelPostReactions
                    post={{
                      ...post,
                      likes: post.likes || 0,
                      shares: post.shares || 0,
                      comments: post.comments || 0,
                      user_reaction_type: post.user_reaction_type || null,
                      reaction_counts: post.reaction_counts || {},
                      reactions_count: post.reactions_count || post.likes || 0,
                    }}
                    onLike={handlePostUpdate}
                    onShare={handleShare}
                    onShowReactions={() => setShowReactionsModal(true)}
                  />
                </div>

                {/* Comments Section - Always Expanded */}
                <div className="post-detail-modal-comments" style={{ marginTop: "16px" }}>
                  <ChannelPostComments
                    post={post}
                    onCommentAdded={(comment) => {
                      handlePostUpdate({
                        comments: (post.comments || 0) + 1,
                      });
                    }}
                    highlightCommentId={highlightCommentId}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Post Reactions Modal */}
      {post && (
        <PostReactionsModal
          isOpen={showReactionsModal}
          onClose={() => setShowReactionsModal(false)}
          postId={post.id}
          currentUser={currentUser}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PostDetailModal;

